import { Breadcrumb, Col, Input, Popconfirm, Row, Space, Table, Tooltip } from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import "../../../styles/Main.scss";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";

const { Search } = Input;

const ArchivedMaterialTypeList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [reload, setReload] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [archiveMaterialTypeList, setArchiveMaterialTypeList] = useState([]);
  const [searchText, setSearchText] = useState("");
  // State initialisation
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  // Fetch Archive Material Type List API
  useEffect(() => {
    const getArchiveMaterialTypeList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("material-type-archive/", state);
        if (apiResponse.data.results.success) {
          setArchiveMaterialTypeList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archive_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getArchiveMaterialTypeList();
  }, [state, reload, t]);

  // Function to handle Table Change
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // Convert sorter field and order to API-compatible sorting enum
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree__name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree__name";
    }
    if (sortField === "type_name" && sortOrder === "ascend") {
      sortingEnumKey = "type_name";
    }
    if (sortField === "type_name" && sortOrder === "descend") {
      sortingEnumKey = "-type_name";
    }
    if (sortField === "hsn_code" && sortOrder === "ascend") {
      sortingEnumKey = "hsn_code";
    }
    if (sortField === "hsn_code" && sortOrder === "descend") {
      sortingEnumKey = "-hsn_code";
    }

    return sortingEnumKey;
  };

  // Callback for handling search input with debounce
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  // Perform search operation and update state
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

// Debounce search input to reduce the number of search requests
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Function to Restore the Archive data
  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("material-type-restore/", {
        deleted: ids,
      });

      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("material_type_restore_successfully"));
        setReload(!reload);
        setSelectedRow([]);
        navigate("/material-type");
      } else {
        ErrorNotificationMsg(t("error_in_restore_material_type"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Material Type",
      dataIndex: "type_name",
      key: "type_name",
      sorter: true,
    },
    {
      title: "Sub Category",
      dataIndex: "sub_category",
      key: "type_name",
      sorter: true,
    },
    {
      title: "HSN Code",
      dataIndex: "hsn_code",
      key: "hsn_code",
      sorter: true,
    },
    {
      title: "GST (%)",
      dataIndex: "gst_rate",
      key: "gst_rate",
      sorter: true,
      width: 150,
      render: (value) => <span>{value} %</span>,
    },
    {
      title: "Escalation (%)",
      dataIndex: "escalation",
      key: "escalation",
      sorter: true,
      width: 210,
      render: (value) => <span> {value} %</span>,
    },
    {
      title: "Action",
      key: "operation",
      width: "100px",
      render: (text, record) => (
        <Space>
           <Tooltip
            color={colors}
            placement="right"
            title={
              <span style={{ color: "#fff" }}>
                Restore
              </span>
            }
          >
          <Popconfirm
            className="action"
            title={t("pop_restore_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore className="ArchiverestorImg" />
          </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
          
              <h3>{t("pl_archived_material_type_list6")}</h3>
       


            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/material-type">{t("pl_material_type4")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList1")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_restore")}
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>
      <div className="stepscontentwrap">
        <div className="stepsContent">
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body" style={{padding:'0'}}>
       
          <Row className="tblheader">
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-heading"
            >
            </Col>
          </Row>
<div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={archiveMaterialTypeList}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
           <div className="FormFooter Container">
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("pl_back_to_list")}
              onClick={() =>
                navigate(
                  `/material-type`
                )
              }
            />
          </div>
        </div>
      </section>
    </div></div></>
  );
};

export default ArchivedMaterialTypeList;
